<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row justify-space-between align-center elevation-3 mb-3">
      <span>Inventory Posting</span>
      <div
        class="d-flex flex-row align-center"
        style="gap: 0.625rem;">
        <v-btn
          color="success"
          rounded
          small
          :loading="loading"
          :disabled="!canPost"
          @click="confirmPostInventory()">
          <v-icon
            small
            class="mr-1">
            mdi-send
          </v-icon>
          Post Physical Inventory
        </v-btn>
        <v-btn
          color="success"
          rounded
          small
          :loading="loading"
          :disabled="!canPost"
          @click="confirmPostStockAdjust()">
          <v-icon
            small
            class="mr-1">
            mdi-send
          </v-icon>
          Post Stock Adjust Out
        </v-btn>
      </div>
    </div>

    <!-- Header Fields -->
    <v-card
      class="elevation-3 mb-3 pa-5"
      :loading="loading">
      <v-row>
        <v-col
          cols="12"
          md="4">
          <v-text-field
            v-model="form.physicalInventoryDocNo"
            label="Physical Inventory Document No. *"
            placeholder="e.g. PI-2026-0001"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.required]" />
        </v-col>
        <v-col
          cols="12"
          md="4">
          <v-menu
            v-model="menuAdjustInDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.adjustInPostingDate"
                label="Adjust In Posting Date *"
                placeholder="YYYY-MM-DD"
                outlined
                dense
                hide-details="auto"
                readonly
                append-icon="mdi-calendar"
                :rules="[rules.required]"
                v-bind="attrs"
                v-on="on" />
            </template>
            <v-date-picker
              v-model="form.adjustInPostingDate"
              no-title
              @input="menuAdjustInDate = false" />
          </v-menu>
        </v-col>
        <v-col
          cols="12"
          md="4">
          <v-menu
            v-model="menuAdjustOutDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.adjustOutPostingDate"
                label="Adjust Out Posting Date *"
                placeholder="YYYY-MM-DD"
                outlined
                dense
                hide-details="auto"
                readonly
                append-icon="mdi-calendar"
                :rules="[rules.required]"
                v-bind="attrs"
                v-on="on" />
            </template>
            <v-date-picker
              v-model="form.adjustOutPostingDate"
              no-title
              @input="menuAdjustOutDate = false" />
          </v-menu>
        </v-col>
      </v-row>
    </v-card>

    <!-- Source Documents -->
    <v-card
      class="elevation-3 mb-3 pa-5"
      :loading="loading">
      <div class="subtitle-2 mb-3">
        <v-icon
          small
          class="mr-1">
          mdi-file-document-outline
        </v-icon>
        Source Documents
        <span class="grey--text text--lighten-1 body-2">(optional — add IDs to pull items)</span>
      </div>
      <v-row>
        <v-col
          cols="12"
          md="4">
          <v-combobox
            v-model="form.orderWarehouseIds"
            label="Order Warehouse IDs"
            placeholder="Type ID and press Enter"
            outlined
            dense
            hide-details="auto"
            multiple
            small-chips
            deletable-chips
            append-icon=""
            @change="onOrderWarehouseIdsChange()" />
        </v-col>
        <v-col
          cols="12"
          md="4">
          <v-combobox
            v-model="form.withdrawNoteCodes"
            label="Withdraw Note Codes"
            placeholder="Type code and press Enter"
            outlined
            dense
            hide-details="auto"
            multiple
            small-chips
            deletable-chips
            append-icon=""
            @change="onWithdrawNoteCodesChange()" />
        </v-col>
        <v-col
          cols="12"
          md="4">
          <v-combobox
            v-model="form.deliveryNoteCodes"
            label="Delivery Note Codes"
            placeholder="Type code and press Enter"
            outlined
            dense
            hide-details="auto"
            multiple
            small-chips
            deletable-chips
            append-icon=""
            @change="onDeliveryNoteCodesChange()" />
        </v-col>
      </v-row>
    </v-card>

    <!-- Empty Documents Warning -->
    <v-alert
      v-if="emptyDocs.length > 0"
      type="warning"
      outlined
      dense
      color="warning"
      class="mb-3"
      style="background-color: white !important;">
      <div class="subtitle-2 mb-1">
        ไม่พบสินค้าในเอกสารต่อไปนี้
      </div>
      <div
        v-for="doc in emptyDocs"
        :key="`${doc.type}-${doc.id}`"
        class="d-flex align-center body-2 mt-1"
        style="gap: 8px;">
        <v-chip
          x-small
          :color="getSourceColor(doc.type)"
          text-color="white">
          {{ doc.label }}
        </v-chip>
        <span>{{ doc.id }}</span>
      </div>
    </v-alert>

    <!-- Items Table -->
    <v-card
      class="elevation-3 pa-5"
      :loading="loading">
      <div class="d-flex justify-space-between align-center mb-3">
        <div class="subtitle-2">
          <v-icon
            small
            class="mr-1">
            mdi-format-list-bulleted
          </v-icon>
          Items
          <span class="grey--text text--lighten-1 body-2">
            ({{ items.length }} items{{ sourceDocCount > 0 ? ` from ${sourceDocCount} documents` : '' }})
          </span>
        </div>
        <v-text-field
          v-model="search"
          placeholder="Search items..."
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-magnify"
          style="max-width: 240px;"
          clearable />
      </div>

      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        :items-per-page="50"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': [25, 50, 100, -1]
        }"
        item-key="uid"
        fixed-header
        dense>
        <template #[`item.color`]="{ item }">
          <div
            class="d-flex align-center"
            style="gap: 6px;">
            <span>{{ item.color }}</span>
          </div>
        </template>
        <template #[`item.size`]="{ item }">
          <span class="text-uppercase">{{ item.size }}</span>
        </template>
        <template #[`item.price`]="{ item }">
          {{ item.price | showNumberFormat() }}
        </template>
        <template #[`item.physicalQuantity`]="{ item }">
          <span class="font-weight-medium">
            {{ item.physicalQuantity | showNumberFormat() }}
          </span>
        </template>
        <template #[`item.source`]="{ item }">
          <v-chip
            x-small
            :color="getSourceColor(item.sourceType)"
            text-color="white">
            {{ item.sourceId }}
          </v-chip>
        </template>
      </v-data-table>

      <!-- Summary -->
      <v-divider class="my-3" />
      <div class="d-flex justify-space-between align-center">
        <div
          class="d-flex"
          style="gap: 16px;">
          <div class="summary-card">
            <span class="caption grey--text">Total items</span>
            <span class="subtitle-1 font-weight-medium ml-2">{{ items.length }}</span>
          </div>
          <div class="summary-card">
            <span class="caption grey--text">Total quantity</span>
            <span class="subtitle-1 font-weight-medium ml-2">{{ totalQuantity | showNumberFormat() }}</span>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Confirm Dialog -->
    <v-dialog
      v-model="confirmDialog"
      max-width="420"
      persistent>
      <v-card>
        <v-card-title class="subtitle-1">
          {{ confirmDialogTitle }}
        </v-card-title>
        <v-card-text>
          <template v-if="postType === 'physical'">
            คุณต้องการ Post Physical Inventory เอกสาร <strong>{{ form.physicalInventoryDocNo }}</strong> จำนวน {{ items.length }} รายการ ใช่หรือไม่?
          </template>
          <template v-else>
            คุณต้องการ Post Stock Adjust Out จำนวน {{ items.length }} รายการ ใช่หรือไม่?
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="confirmDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            :loading="loading"
            @click="executePost()">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Result Dialog -->
    <v-dialog
      v-model="resultDialog"
      max-width="420">
      <v-card>
        <v-card-title class="subtitle-1">
          <v-icon
            color="success"
            class="mr-2">
            mdi-check-circle
          </v-icon>
          Stock Adjust Out สำเร็จ
        </v-card-title>
        <v-card-text>
          <div class="body-2 mb-2">
            เลขที่เอกสารที่ได้จากระบบ:
          </div>
          <v-alert
            type="info"
            dense
            outlined
            class="mb-0">
            <span class="font-weight-medium">{{ resultDocNo }}</span>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="success"
            @click="resultDialog = false">
            ตกลง
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import InventoryPostingProvider from '@/resources/InventoryPostingProvider'

const InventoryPostingService = new InventoryPostingProvider()

export default {
  data () {
    return {
      loading: false,
      search: '',
      confirmDialog: false,
      resultDialog: false,
      resultDocNo: '',
      postType: '',
      menuAdjustInDate: false,
      menuAdjustOutDate: false,
      form: {
        physicalInventoryDocNo: '',
        adjustInPostingDate: '',
        adjustOutPostingDate: '',
        orderWarehouseIds: [],
        withdrawNoteCodes: [],
        deliveryNoteCodes: []
      },
      items: [],
      emptyDocs: [],
      headers: [
        { text: 'Item No.', value: 'itemNo', width: '160px' },
        { text: 'Name', value: 'name', width: '240px' },
        { text: 'Color', value: 'color', width: '100px' },
        { text: 'Size', value: 'size', width: '70px', align: 'center' },
        { text: 'Price', value: 'price', width: '100px', align: 'end' },
        { text: 'Quantity', value: 'physicalQuantity', width: '100px', align: 'end' },
        { text: 'Warehouse', value: 'companyLocation', width: '120px' },
        { text: 'Source', value: 'source', width: '120px', sortable: false }
      ],
      rules: {
        required: (v) => !!v || 'This field is required'
      }
    }
  },
  computed: {
    totalQuantity () {
      return this.items.reduce((sum, item) => sum + (item.physicalQuantity || 0), 0)
    },
    sourceDocCount () {
      const sources = new Set(this.items.map((item) => item.sourceId))
      return sources.size
    },
    canPost () {
      return this.form.physicalInventoryDocNo
        && this.form.adjustInPostingDate
        && this.form.adjustOutPostingDate
        && this.items.length > 0
    },
    confirmDialogTitle () {
      return this.postType === 'physical'
        ? 'Confirm Post Physical Inventory'
        : 'Confirm Post Stock Adjust Out'
    },
    confirmDialogAction () {
      return this.postType === 'physical'
        ? 'Post Physical Inventory'
        : 'Post Stock Adjust Out'
    }
  },
  methods: {
    ...mapActions('Components', ['setSnackbar']),
    async onOrderWarehouseIdsChange () {
      this.removeEmptyDocsByType('order')

      if (!this.form.orderWarehouseIds.length) {
        this.removeItemsBySourceType('order')
        return
      }

      try {
        this.loading = true
        const { data } = await InventoryPostingService.getSkusByOrderWarehouseIds(this.form.orderWarehouseIds)
        this.removeItemsBySourceType('order')
        const mapped = data.map((item, index) => ({
          ...item,
          uid: `order-${item.ref}-${item.skuId || index}`,
          sourceType: 'order',
          sourceId: item.ref
        }))
        this.items.push(...mapped)
        this.checkEmptyDocs(this.form.orderWarehouseIds, data, 'order', 'Order Warehouse')
      } catch (error) {
        console.error('onOrderWarehouseIdsChange', error)
        this.setSnackbar({
          value: true,
          message: error?.message || 'Failed to fetch order warehouse items',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async onWithdrawNoteCodesChange () {
      this.removeEmptyDocsByType('withdrawNote')

      if (!this.form.withdrawNoteCodes.length) {
        this.removeItemsBySourceType('withdrawNote')
        return
      }

      try {
        this.loading = true
        const { data } = await InventoryPostingService.getSkusByWithdrawNoteCodes(this.form.withdrawNoteCodes)
        this.removeItemsBySourceType('withdrawNote')
        const mapped = data.map((item, index) => ({
          ...item,
          uid: `wn-${item.ref}-${item.skuId || index}`,
          sourceType: 'withdrawNote',
          sourceId: item.ref
        }))
        this.items.push(...mapped)
        this.checkEmptyDocs(this.form.withdrawNoteCodes, data, 'withdrawNote', 'Withdraw Note')
      } catch (error) {
        console.error('onWithdrawNoteCodesChange', error)
        this.setSnackbar({
          value: true,
          message: error?.message || 'Failed to fetch withdraw note items',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async onDeliveryNoteCodesChange () {
      this.removeEmptyDocsByType('deliveryNote')

      if (!this.form.deliveryNoteCodes.length) {
        this.removeItemsBySourceType('deliveryNote')
        return
      }

      try {
        this.loading = true
        const { data } = await InventoryPostingService.getSkusByDeliveryNoteCodes(this.form.deliveryNoteCodes)
        this.removeItemsBySourceType('deliveryNote')
        const mapped = data.map((item, index) => ({
          ...item,
          uid: `dn-${item.ref}-${item.skuId || index}`,
          sourceType: 'deliveryNote',
          sourceId: item.ref
        }))
        this.items.push(...mapped)
        this.checkEmptyDocs(this.form.deliveryNoteCodes, data, 'deliveryNote', 'Delivery Note')
      } catch (error) {
        console.error('onDeliveryNoteCodesChange', error)
        this.setSnackbar({
          value: true,
          message: error?.message || 'Failed to fetch delivery note items',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    checkEmptyDocs (inputIds, responseData, sourceType, label) {
      const returnedRefs = new Set(responseData.map((item) => item.ref))
      const empty = inputIds.filter((id) => !returnedRefs.has(id))
      empty.forEach((id) => {
        this.emptyDocs.push({ type: sourceType, label, id })
      })
    },
    removeEmptyDocsByType (sourceType) {
      this.emptyDocs = this.emptyDocs.filter((doc) => doc.type !== sourceType)
    },
    removeItemsBySourceType (sourceType) {
      this.items = this.items.filter((item) => item.sourceType !== sourceType)
    },
    getColorHex (colorName) {
      const colorMap = {
        black: '#333333',
        white: '#F5F5F5',
        yellow: '#F5D76E',
        cream: '#FFFDD0',
        red: '#E24B4A',
        pink: '#ED93B1',
        blue: '#85B7EB',
        green: '#97C459',
        navy: '#1B2A4A',
        beige: '#D4C5A9',
        brown: '#8B6914',
        grey: '#999999',
        gray: '#999999',
        orange: '#EF9F27',
        purple: '#AFA9EC'
      }
      return colorMap[colorName?.toLowerCase()] || '#CCCCCC'
    },
    getSourceColor (sourceType) {
      const colorMap = {
        order: 'info',
        withdrawNote: 'warning',
        deliveryNote: 'success'
      }
      return colorMap[sourceType] || 'grey'
    },
    confirmPostInventory () {
      this.postType = 'physical'
      this.confirmDialog = true
    },
    confirmPostStockAdjust () {
      this.postType = 'stockAdjust'
      this.confirmDialog = true
    },
    groupItems (items) {
      const groupItem = []
      for (const item of items) {
        const index = groupItem.findIndex((gi) => gi.skuId === item.skuId && gi.warehouse.id === item.warehouse.id)
        if (index === -1) {
          groupItem.push({ ...item })
        } else {
          groupItem[index].physicalQuantity += item.physicalQuantity
        }
      }
      return groupItem
    },
    async executePost () {
      if (this.postType === 'physical') {
        await this.executePostPhysicalInventory()
      } else {
        await this.executePostStockAdjustOut()
      }
    },
    async executePostPhysicalInventory () {
      try {
        this.loading = true
        const docNo = this.form.physicalInventoryDocNo
        const postingDate = this.form.adjustInPostingDate

        // Step 1: Group SKU เดียวกัน + warehouse เดียวกัน
        const grouped = this.groupItems(this.items)
        const validItems = grouped.filter((item) => item.physicalQuantity > 0)

        // Step 2: Update เอกสาร Stock (Physical Inventory)
        await InventoryPostingService.updateReference(docNo, validItems)

        // Step 3: Post (Approve) เอกสาร
        await InventoryPostingService.approveReference(docNo, postingDate)

        this.confirmDialog = false
        this.setSnackbar({
          value: true,
          message: `Physical Inventory ${docNo} posted successfully`,
          type: 'success'
        })
      } catch (error) {
        console.error('executePostPhysicalInventory', error)
        this.setSnackbar({
          value: true,
          message: error?.message || 'Failed to post physical inventory',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async executePostStockAdjustOut () {
      try {
        this.loading = true
        const postingDate = this.form.adjustOutPostingDate

        // Step 1: Group SKU เดียวกัน + warehouse เดียวกัน
        const grouped = this.groupItems(this.items)

        // Step 2: สร้าง payload สำหรับ stock adjust negative
        const payload = {
          postingDate,
          stockAdjustmentLines: grouped.map((item) => {
            const codes = item.itemNo.split('.')
            return {
              itemNo: item.itemNo,
              description: item.name,
              locationCode: item.warehouse.code,
              uom: 'PCS',
              quantity: item.physicalQuantity,
              model: `${codes[0]}.${codes[4]}.${codes[5]}`,
              companyLocation: item.warehouse.code,
              genBusPostingGroup: 'STOCK_COUNT'
            }
          })
        }

        // Step 3: Post stock adjust negative
        const { data } = await InventoryPostingService.postStockAdjustNegative(payload)

        this.confirmDialog = false
        this.resultDocNo = data?.documentNo || data?.id || JSON.stringify(data)
        this.resultDialog = true
      } catch (error) {
        console.error('executePostStockAdjustOut', error)
        this.setSnackbar({
          value: true,
          message: error?.message || 'Failed to post stock adjust out',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.toolbar {
  padding: 8px 16px;
  border-radius: 4px;
  background: white;
}
.color-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
}
.summary-card {
  background: #f5f5f5;
  border-radius: 4px;
  padding: 6px 16px;
  display: flex;
  align-items: center;
}
</style>
